import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    projectScope: '',
    timeline: '',
    budget: '',
    requirements: '',
    preferredContact: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const inquiryTypes = [
    { value: 'consultation', label: 'Auditoría de Automatización' },
    { value: 'development', label: 'Desarrollo de Agentes IA' },
    { value: 'migration', label: 'Sistemas Core Evolutivos' },
    { value: 'support', label: 'Soporte Técnico y Monitoreo' },
    { value: 'training', label: 'Capacitación en IA' }
  ];

  const projectScopes = [
    { value: 'small', label: 'Pequeña (1-10 usuarios)' },
    { value: 'medium', label: 'Mediana (11-50 usuarios)' },
    { value: 'large', label: 'Grande (51-200 usuarios)' },
    { value: 'enterprise', label: 'Empresarial (200+ usuarios)' }
  ];

  const timelines = [
    { value: 'urgent', label: 'Urgente (1-2 semanas)' },
    { value: 'short', label: 'Corto plazo (1-3 meses)' },
    { value: 'medium', label: 'Mediano plazo (3-6 meses)' },
    { value: 'long', label: 'Largo plazo (6+ meses)' }
  ];

  const budgetRanges = [
    { value: 'under-10k', label: 'Menos de $10,000' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: 'over-100k', label: 'Más de $100,000' }
  ];

  const contactMethods = [
    { value: 'email', label: 'Correo electrónico' },
    { value: 'phone', label: 'Llamada telefónica' },
    { value: 'video', label: 'Videollamada' },
    { value: 'in-person', label: 'Reunión presencial' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de correo electrónico inválido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'El nombre de la empresa es requerido';
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = 'Selecciona el tipo de consulta';
    }

    if (!formData.requirements.trim()) {
      newErrors.requirements = 'Describe tus requerimientos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        projectScope: '',
        timeline: '',
        budget: '',
        requirements: '',
        preferredContact: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="bg-white rounded-2xl p-8 border border-black/10">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircle" size={32} color="white" />
          </div>
          <h3 className="text-2xl font-semibold text-black mb-4">
            ¡Mensaje Enviado!
          </h3>
          <p className="text-black/60 mb-6">
            Gracias por contactarnos. Nuestro equipo de expertos analizará tu solicitud y se pondrá en contacto contigo a la brevedad.
          </p>
          <Button
            variant="outline"
            onClick={() => setShowSuccess(false)}
            iconName="ArrowLeft"
            iconPosition="left"
            className="border-black/20 text-black hover:bg-black/5"
          >
            Enviar Otro Mensaje
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 border border-black/10">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-[26px] font-medium text-black mb-4" style={{ letterSpacing: '-0.005em' }}>
          Solicita tu Consulta
        </h2>
        <p className="text-black/60">
          Completa el formulario y nuestro equipo de expertos en IA se pondrá en contacto contigo para discutir las necesidades de automatización de tu empresa.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Nombre Completo"
            type="text"
            name="name"
            placeholder="Tu nombre completo"
            value={formData.name}
            onChange={handleInputChange}
            error={errors.name}
            required
          />
          
          <Input
            label="Correo Electrónico"
            type="email"
            name="email"
            placeholder="tu@empresa.com"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Teléfono"
            type="tel"
            name="phone"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={handleInputChange}
            error={errors.phone}
            required
          />
          
          <Input
            label="Empresa"
            type="text"
            name="company"
            placeholder="Nombre de tu empresa"
            value={formData.company}
            onChange={handleInputChange}
            error={errors.company}
            required
          />
        </div>

        {/* Project Details */}
        <div className="border-t border-black/10 pt-6">
          <h3 className="text-lg font-semibold text-black mb-4">
            Detalles del Proyecto
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Tipo de Consulta"
              options={inquiryTypes}
              value={formData.inquiryType}
              onChange={(value) => handleSelectChange('inquiryType', value)}
              error={errors.inquiryType}
              placeholder="Selecciona el tipo"
              required
            />
            
            <Select
              label="Alcance del Proyecto"
              options={projectScopes}
              value={formData.projectScope}
              onChange={(value) => handleSelectChange('projectScope', value)}
              placeholder="Tamaño de la empresa"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Select
              label="Cronograma"
              options={timelines}
              value={formData.timeline}
              onChange={(value) => handleSelectChange('timeline', value)}
              placeholder="¿Cuándo necesitas el proyecto?"
            />
            
            <Select
              label="Presupuesto Estimado"
              options={budgetRanges}
              value={formData.budget}
              onChange={(value) => handleSelectChange('budget', value)}
              placeholder="Rango de presupuesto"
            />
          </div>
        </div>

        {/* Requirements */}
        <div>
          <label className="block text-sm font-medium text-black/70 mb-2">
            Requerimientos Detallados *
          </label>
          <textarea
            name="requirements"
            rows={6}
            className="w-full px-4 py-3 bg-black/[0.03] border border-black/10 rounded-lg text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent resize-none"
            placeholder="Describe tus necesidades específicas, cuellos de botella actuales, y objetivos que esperas alcanzar con nuestros sistemas autónomos..."
            value={formData.requirements}
            onChange={handleInputChange}
          />
          {errors.requirements && (
            <p className="mt-1 text-sm text-error">{errors.requirements}</p>
          )}
        </div>

        {/* Contact Preference */}
        <Select
          label="Método de Contacto Preferido"
          options={contactMethods}
          value={formData.preferredContact}
          onChange={(value) => handleSelectChange('preferredContact', value)}
          placeholder="¿Cómo prefieres que te contactemos?"
        />

        {/* Submit Button */}
        <div className="pt-6">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isSubmitting}
            className="bg-black hover:opacity-80 text-white font-medium"
            iconName="Send"
            iconPosition="right"
          >
            {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
          </Button>
        </div>

        {/* Additional Info */}
        <div className="text-center pt-4">
          <p className="text-sm text-black/50">
            Al enviar este formulario, aceptas nuestros términos de servicio y política de privacidad.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;